import type { HelpArticle } from './types';

type Scored = { article: HelpArticle; score: number; matched: number; strong: number };

/**
 * Words carrying no signal about WHICH article is wanted.
 *
 * "mucka" is in here deliberately: every article is about Mucka, so leaving it
 * in scored a point against practically the whole knowledge base and let one
 * incidental word outrank a precise match. A user asking "how much does Mucka
 * cost" was landing on an invoice-numbering article, because that summary
 * happened to contain more filler words than the pricing one (TKT-1051).
 *
 * Keep this list to genuine filler. Anything a user might be searching FOR
 * ("cost", "price", "job", "quote") must stay out of it.
 */
const STOPWORDS = new Set<string>([
  // Short function words. Single characters are dropped separately by length.
  'am', 'an', 'as', 'at', 'be', 'by', 'do', 'if', 'in', 'is', 'it', 'me',
  'my', 'no', 'of', 'on', 'or', 'so', 'to', 'up', 'us', 'we',
  'the', 'and', 'for', 'with', 'from', 'that', 'this', 'these', 'those',
  'how', 'what', 'when', 'where', 'which', 'who', 'why', 'does', 'did',
  'can', 'could', 'would', 'should', 'will', 'shall', 'may', 'might',
  'you', 'your', 'yours', 'our', 'ours', 'its', 'their',
  'are', 'was', 'were', 'been', 'being', 'have', 'has', 'had',
  'about', 'into', 'onto', 'over', 'under', 'out', 'off',
  'much', 'many', 'any', 'some', 'all', 'get', 'got', 'set',
  'not', 'but', 'than', 'then', 'there', 'here',
  'mucka',
]);

/**
 * A hit must cover at least this share of the query's meaningful terms.
 *
 * Without it, one incidental substring match was enough to return an article:
 * "zzzqqqxx nonsense term" came back with a payment-terms article because
 * "term" is a substring of "terms". The engine then tried to answer from a
 * completely unrelated article rather than saying it didn't know.
 */
const MIN_TERM_COVERAGE = 0.5;

function scoreArticle(
  article: HelpArticle,
  terms: string[],
): { score: number; matched: number; strong: number } {
  let score = 0;
  let matched = 0;
  let strong = 0;
  const title = article.title.toLowerCase();
  const summary = article.summary.toLowerCase();
  const keywords = (article.keywords ?? []).map((k) => k.toLowerCase());

  for (const term of terms) {
    let termScore = 0;
    // Title and keyword hits are STRONG: both are deliberate, curated signals
    // about what an article is for. A summary hit is weak — summaries are
    // prose, so they collide with words that say nothing about the topic
    // ("cost of Mucka" was landing on a calendar article off its summary).
    let isStrong = false;
    if (title.includes(term)) { termScore += 8; isStrong = true; }
    if (title.startsWith(term)) termScore += 4;
    if (keywords.some((k) => k.includes(term))) { termScore += 5; isStrong = true; }
    // An exact keyword hit is the strongest signal we have — keywords are
    // hand-curated per article precisely to catch how users phrase things.
    if (keywords.some((k) => k === term)) termScore += 6;
    if (summary.includes(term)) termScore += 3;
    if (termScore > 0) matched++;
    if (isStrong) strong++;
    score += termScore;
  }

  // Reward breadth: an article matching two of the query's terms beats one
  // matching a single term very loudly. Without this, a title containing one
  // common word outranked a genuinely on-topic article.
  return { score: score * (1 + (matched - 1) * 0.5), matched, strong };
}

/**
 * The meaningful words in a query — the same tokenising and the same stopword
 * list `searchArticles` ranks on.
 *
 * Exported because `executeFindHelpArticle` has to decide which PART of a long
 * article to hand over, and it must make that decision on the same terms the
 * search made its own. Two tokenisers would drift, and the drift would be
 * invisible: an article correctly found, then clipped to the section that
 * happens to match a slightly different word list.
 */
export function queryTerms(query: string): string[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [];
  return trimmed
    .split(/[^a-z0-9]+/)
    .filter(t => t.length > 1 && !STOPWORDS.has(t));
}

export function searchArticles(
  query: string,
  articles: HelpArticle[],
  limit = 5,
): HelpArticle[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [];

  const raw = trimmed
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1);
  // Drop filler — but if the query is nothing BUT filler, fall back to the raw
  // terms rather than returning nothing at all.
  // A query with no meaningful word left ("how much is Mucka?") has nothing to
  // rank on. Returning nothing is the honest answer — the caller then says so,
  // or answers from what it already knows. Falling back to the filler terms
  // just surfaced whichever articles happened to have "Mucka" in the title.
  const terms = raw.filter((t) => !STOPWORDS.has(t));
  if (terms.length === 0) return [];

  const required = Math.max(1, Math.ceil(terms.length * MIN_TERM_COVERAGE));

  const scored: Scored[] = articles
    .map((a) => ({ article: a, ...scoreArticle(a, terms) }))
    // `strong >= 1` is what stops a stray summary word qualifying an article
    // on its own — the difference between "no article covers this, say so"
    // and confidently answering from something unrelated.
    .filter((s) => s.score > 0 && s.matched >= required && s.strong >= 1)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((s) => s.article);
}
